import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import { api } from './services/apiClient';
import { AIChatProvider, AIChatWidget } from './components/AIChat';
import { ConciergeWidget } from './components/concierge';
import { OfflineIndicator, SyncStatus } from './components/offline';
import { PageSkeleton, LazyCopilotKit } from './components/common';
import { PurchaseInterceptionModal, usePurchaseInterception } from './components/PurchaseInterception';
import type { PurchaseAction } from './components/PurchaseInterception';

// Lazy load ALL route components for better code splitting
// This significantly reduces the initial bundle size and improves FCP/LCP

// Core pages - lazy loaded for performance
const Dashboard = lazy(() => import('./components/Dashboard'));
const Login = lazy(() => import('./components/Login'));

// Route guards
const OnboardingGuard = lazy(() => import('./components/OnboardingGuard'));

// Onboarding pages - lazy loaded as they're not needed on every visit
const OnboardingWelcome = lazy(() => import('./pages/onboarding/OnboardingWelcome'));
const TellerConnect = lazy(() => import('./pages/onboarding/TellerConnect'));
const CSVUpload = lazy(() => import('./pages/onboarding/CSVUpload'));
const OnboardingProgress = lazy(() => import('./pages/onboarding/OnboardingProgress'));
const OnboardingComplete = lazy(() => import('./pages/onboarding/OnboardingComplete'));

// Decision pages - lazy loaded
const TrafficLightDecision = lazy(() => import('./pages/decisions/TrafficLightDecision'));

// AG-UI components - lazy loaded to reduce initial bundle size
// These components include CopilotKit which is ~1.2MB
const SmartCategorizer = lazy(() => import('./components/SmartCategorizer').then(module => ({ default: module.SmartCategorizer })));
const TransactionReview = lazy(() => import('./pages/TransactionReview').then(module => ({ default: module.TransactionReview })));

// Landing pages - public, no auth required, standalone layout
const LandingPage = lazy(() => import('./pages/LandingPage'));
const BusinessLanding = lazy(() => import('./pages/BusinessLanding'));

// Bills settings - lazy loaded
const BillsSettingsPage = lazy(() => import('./pages/BillsSettingsPage'));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'));
const BusinessHealthReportsPage = lazy(() => import('./pages/BusinessHealthReportsPage'));

// Test Dashboard - only available in development/test mode
const TestDashboard = lazy(() => import('./pages/TestDashboard'));

// Check if we're in development or test mode
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'test';

/**
 * PurchaseInterceptionWrapper
 *
 * Wraps the PurchaseInterceptionModal and usePurchaseInterception hook.
 * Only activates when user is authenticated (has authToken).
 * Provides real-time purchase interception across all routes.
 */
function PurchaseInterceptionWrapper() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check auth status on mount and when storage changes
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      setIsAuthenticated(!!token);
    };

    checkAuth();

    // Listen for storage changes (e.g., login/logout in another tab)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Only use the hook when authenticated to avoid unnecessary WebSocket connections
  const interception = usePurchaseInterception({
    enableRealtime: isAuthenticated,
    autoShowModal: true,
  });

  // Don't render modal if not authenticated or no active purchase
  if (!isAuthenticated) {
    return null;
  }

  // Adapter function to match modal's onAction signature with hook's handleAction
  const handleModalAction = async (action: PurchaseAction, _purchaseId: string) => {
    await interception.handleAction(action);
  };

  return (
    <PurchaseInterceptionModal
      open={interception.isModalOpen}
      onClose={interception.closeModal}
      purchase={interception.activePurchase}
      decision={interception.activeDecision ? {
        decision: interception.activeDecision.decision,
        confidence: interception.activeDecision.confidence,
        reasoning: interception.activeDecision.reasoning,
        impact: interception.activeDecision.impact,
        recommendation: interception.activeDecision.recommendation,
        alternatives: interception.activeDecision.alternatives,
      } : null}
      isLoading={interception.isDecisionLoading}
      currentBalance={interception.currentBalance}
      onAction={handleModalAction}
    />
  );
}

/**
 * AppContent - Inner component that has access to Router context.
 * Renders the landing page standalone (no app shell) or the full app shell for other routes.
 */
function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/welcome';
  const isBusinessLanding = location.pathname === '/business';
  const isStandalonePage = isLandingPage || isBusinessLanding;

  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);
  const [onboardingProgress, setOnboardingProgress] = useState<number>(0);

  useEffect(() => {
    // PWA install prompt handling
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    // Skip onboarding check on standalone landing pages
    if (isStandalonePage) return;

    // Check onboarding status on mount
    const checkOnboardingStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        const response = await api.onboarding.checkStatus();
        setHasCompletedOnboarding(response.data.hasCompletedOnboarding);
        setOnboardingProgress(response.data.progress);
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // On error, assume onboarding incomplete — user should see the
        // "Connect your bank" screen, not an empty dashboard.
        setHasCompletedOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [isStandalonePage, location.pathname]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted PWA install');
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  // Landing pages render standalone — no app shell, no header, no footer
  if (isStandalonePage) {
    return (
      <Suspense fallback={<PageSkeleton variant="dashboard" title="Loading" />}>
        {isBusinessLanding ? <BusinessLanding /> : <LandingPage />}
      </Suspense>
    );
  }

  return (
    <>
      {/* Offline status indicator - shows when offline or with stale data */}
      <OfflineIndicator position="top" showWhenOnline={false} />

      <div className="app">
        <header className="app-header">
          <div className="header-content">
            <h1>
              <span style={{ color: '#7C3AED', textShadow: '0 0 8px rgba(255,255,255,0.8), 0 0 2px white' }}>Flow</span>
              <span style={{ color: '#059669', textShadow: '0 0 8px rgba(255,255,255,0.8), 0 0 2px white' }}>In</span>
              <span style={{ color: '#7C3AED', textShadow: '0 0 8px rgba(255,255,255,0.8), 0 0 2px white' }}>Cash</span>
            </h1>
            <nav>
              <Link to="/">Dashboard</Link>
              {!hasCompletedOnboarding && (
                <Link to="/onboarding" style={{ fontWeight: 600, color: '#667eea' }}>
                  {onboardingProgress > 0 && onboardingProgress < 100
                    ? `Resume Setup (${onboardingProgress}%)`
                    : 'Get Started'}
                </Link>
              )}
              <Link to="/decisions">Decisions</Link>
              <Link to="/transactions">Transactions</Link>
              <Link to="/subscriptions">Subscriptions</Link>
              <Link to="/categorizer">Categorize</Link>
              {isDevelopment && (
                <Link to="/test-dashboard" style={{ color: '#6366f1' }}>Tests</Link>
              )}
              <Link to="/login">Login</Link>
            </nav>
          </div>
          {showInstallButton && hasCompletedOnboarding === true && (
            <div className="install-prompt">
              <button
                aria-describedby="install-prompt-explanation"
                className="install-button"
                onClick={handleInstallClick}
              >
                Add to home screen
              </button>
              <p id="install-prompt-explanation">
                Optional — adds a shortcut to this device. Your account and data stay the same.
              </p>
            </div>
          )}
        </header>

        <main id="main-content" className="app-main" role="main" aria-label="Main content">
          <ErrorBoundary
            onError={(error, errorInfo) => {
              console.error('Application Error:', error, errorInfo);
            }}
          >
            <Suspense fallback={<PageSkeleton variant="dashboard" title="Loading page" />}>
              <Routes>
                {/* Dashboard - main entry point */}
                <Route
                  path="/"
                  element={
                    <OnboardingGuard>
                      <Suspense fallback={<PageSkeleton variant="dashboard" title="Loading dashboard" />}>
                        <Dashboard />
                      </Suspense>
                    </OnboardingGuard>
                  }
                />

                {/* Onboarding Routes */}
                <Route
                  path="/onboarding"
                  element={
                    <Suspense fallback={<PageSkeleton variant="form" title="Loading onboarding" />}>
                      <OnboardingWelcome />
                    </Suspense>
                  }
                />
                <Route
                  path="/onboarding/connect"
                  element={
                    <Suspense fallback={<PageSkeleton variant="form" title="Loading bank connection" />}>
                      <TellerConnect />
                    </Suspense>
                  }
                />
                <Route
                  path="/onboarding/csv"
                  element={
                    <Suspense fallback={<PageSkeleton variant="form" title="Loading CSV upload" />}>
                      <CSVUpload />
                    </Suspense>
                  }
                />
                <Route
                  path="/onboarding/progress/:onboardingId"
                  element={
                    <Suspense fallback={<PageSkeleton variant="detail" title="Loading progress" />}>
                      <OnboardingProgress />
                    </Suspense>
                  }
                />
                <Route
                  path="/onboarding/complete/:onboardingId"
                  element={
                    <Suspense fallback={<PageSkeleton variant="detail" title="Loading completion" />}>
                      <OnboardingComplete />
                    </Suspense>
                  }
                />
                <Route
                  path="/onboarding/complete"
                  element={
                    <Suspense fallback={<PageSkeleton variant="detail" title="Loading completion" />}>
                      <OnboardingComplete />
                    </Suspense>
                  }
                />

                {/* Decision Routes */}
                <Route
                  path="/decisions"
                  element={
                    <Suspense fallback={<PageSkeleton variant="detail" title="Loading decisions" />}>
                      <TrafficLightDecision />
                    </Suspense>
                  }
                />

                {/* Transaction Review */}
                <Route
                  path="/transactions"
                  element={
                    <Suspense fallback={<PageSkeleton variant="list" title="Loading transactions" />}>
                      <TransactionReview />
                    </Suspense>
                  }
                />

                {/* AI Categorizer */}
                <Route
                  path="/categorizer"
                  element={
                    <Suspense fallback={<PageSkeleton variant="list" title="Loading AI categorizer" />}>
                      <SmartCategorizer />
                    </Suspense>
                  }
                />

                {/* Bills Settings */}
                <Route
                  path="/bills"
                  element={
                    <Suspense fallback={<PageSkeleton variant="list" title="Loading bills" />}>
                      <BillsSettingsPage />
                    </Suspense>
                  }
                />

                {/* Core Subscription Analysis */}
                <Route
                  path="/subscriptions"
                  element={
                    <Suspense fallback={<PageSkeleton variant="list" title="Loading subscriptions" />}>
                      <SubscriptionsPage audience="personal" />
                    </Suspense>
                  }
                />
                <Route
                  path="/business/subscriptions"
                  element={
                    <Suspense fallback={<PageSkeleton variant="list" title="Loading business subscriptions" />}>
                      <SubscriptionsPage audience="business" />
                    </Suspense>
                  }
                />
                <Route
                  path="/business/health-reports"
                  element={
                    <Suspense fallback={<PageSkeleton variant="list" title="Loading business health reports" />}>
                      <BusinessHealthReportsPage />
                    </Suspense>
                  }
                />

                {/* Login */}
                <Route
                  path="/login"
                  element={
                    <Suspense fallback={<PageSkeleton variant="form" title="Loading login" />}>
                      <Login />
                    </Suspense>
                  }
                />

                {/* Test Dashboard Route - only in development/test mode */}
                {isDevelopment && (
                  <Route
                    path="/test-dashboard"
                    element={
                      <Suspense fallback={<PageSkeleton variant="list" title="Loading test dashboard" />}>
                        <TestDashboard />
                      </Suspense>
                    }
                  />
                )}
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>

        <footer className="app-footer">
          <p>FlowInCash - Smart Financial Management</p>
          {/* Sync status for pending offline actions */}
          <SyncStatus showDetails={true} />
        </footer>
      </div>
      {/* AI Chat Widget - floating button and chat window */}
      <AIChatWidget />
      {/* Concierge - financial advisor chat via sidecar */}
      <ConciergeWidget />
      {/* Purchase Interception Modal - global for authenticated users */}
      <PurchaseInterceptionWrapper />
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <AIChatProvider>
        <LazyCopilotKit runtimeUrl="/api/copilotkit">
          <Router>
            <AppContent />
          </Router>
        </LazyCopilotKit>
      </AIChatProvider>
    </ToastProvider>
  );
}

export default App;
